import React, { useEffect, useState } from 'react';

import TextInput from './TextInput';
import EmailInput from './EmailInput';
import RadioInput from './RadioInput';
import CheckboxInput from './CheckboxInput';
import SwitchInput from "./SwitchInput";
import Stack from "../../ui/Stack";
import Error from "../../ui/Error";
import SelectOption from "./SelectOption";
import PasswordInput from "./PasswordInput";
import DateInput from "./DateInput";
import Textarea from "./Textarea";
import MultiSelectOption from "./MultiSelectOption";
import Button from "../button/Button";
import TextEditor from "./TextEditor";
import FileInput from "./FileInput";


const renderFields = (schema, data, inline, errors, handleChange, handleBlur, prefix = '', mainData = null) => {
    if (mainData === null) mainData = data;
    return (
        <div className="row align-items-start g-4">
            {Object.keys(schema).map((key) => {
                const field = schema[key];
                const value = data[key];
                const error = errors && errors[key] ? errors[key] : undefined;
                const fieldName = prefix ? `${prefix}.${key}` : key;
                const inputProps = {
                    name: fieldName,
                    label: field.label,
                    value: value,
                    error: typeof error === 'string' ? error : undefined,
                    onChange: handleChange,
                    onBlur: handleBlur,
                    columns: field.columns,
                    inline: inline,
                };

                function getValueByPath(data, path) {
                    return path.reduce((obj, key) => obj?.[key], data);
                }

                if (field.dependsOn) {
                    const shouldRender = field.dependsOn.some(condition => {
                        const path = condition.field.split('.'); // e.g. ['schedules', 'frequency']
                        const value = getValueByPath(mainData, path);
                        return value === condition.value;
                    });

                    if (!shouldRender) return null;
                }

                if (field.type === 'object') {
                    return (
                        <div key={key} className={`${inline ? 'd-flex flex-wrap ' : ''}col-12`}>
                            {field.label && <label className={`form-label opacity-75${inline ? ' mb-0 py-2 col-12 col-md-4' : ''}`}>{field.label}</label>}
                            <div className={`${inline ? 'col' : ''}`}>
                                {renderFields(field.properties, value, inline, error, handleChange, handleBlur, fieldName, mainData)}
                            </div>
                        </div>
                    );
                }

                switch (field.type) {
                    case 'text':
                        return <TextInput key={key} {...inputProps} placeholder={field.placeholder} />;
                    case 'email':
                        return <EmailInput key={key} {...inputProps} />;
                    case 'radio':
                        return <RadioInput key={key} {...inputProps} options={field.options} />;
                    case 'checkbox':
                        return <CheckboxInput key={key} {...inputProps} />;
                    case 'switch':
                        return <SwitchInput key={key} {...inputProps} aligned={field.aligned} />;
                    case 'select':
                        return <SelectOption key={key} {...inputProps} enums={field.enums} options={field.options} searchPlaceholder={field.searchPlaceholder} isSearch={field.isSearch} />;
                    case 'multiselect':
                        return <MultiSelectOption key={key} {...inputProps} options={field.options} isSearch={field.isSearch} searchPlaceholder={field.searchPlaceholder} />;
                    case 'date':
                        return <DateInput key={key} {...inputProps} max={field.max} options={field.options} />;
                    case 'textarea':
                        return <Textarea key={key} {...inputProps} />;
                    case 'file':
                        return <FileInput key={key} {...inputProps} placeholder={field.placeholder} isMultiple={field.isMultiple} />;
                    case 'password':
                        return <PasswordInput key={key} {...inputProps} />;
                    case 'editor':
                        return <TextEditor key={key} {...inputProps} />
                    default:
                        return null;
                }
            })}
        </div>
    );
};

const Form = ({ schema, initialData, onFormSubmit, onInputChange = () => { }, onFormCancel, status, error, inline = false, updatedData }) => {
    const [data, setData] = useState(initialData);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (updatedData) {
            setData(updatedData);
        }
    }, [updatedData]);

    const updateNestedObject = (obj, keys, value) => {
        if (keys.length === 1) {
            return { ...obj, [keys[0]]: value };
        }
        const [key, ...restKeys] = keys;
        return {
            ...obj,
            [key]: updateNestedObject(obj[key] || {}, restKeys, value)
        };
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        // Determine the value to be set (checkbox , multiselect or regular input)

        let newValue;
        if (type === 'checkbox') {
            newValue = checked;
        } else if (type === 'select-multiple') {
            newValue = Array.from(event.target.options)
                .filter(option => option.selected)
                .map(option => option.value);
        } else {
            newValue = value;
        }

        // Split the name into keys for nested access
        const keys = name.split('.');

        // Use the updateNestedObject function to create a new data object with the updated value
        const newData = updateNestedObject(data, keys, newValue);

        // Set the new data
        setData(newData);
        onInputChange(name, newData);
    };



    const handleBlur = (event) => {
        const { name } = event.target;
        const keys = name.split('.');
        let tempSchema = schema;
        let newErrors = { ...errors };
        let tempData = data;
        let fieldsData = data;

        keys.reduce((parentErrors, key, index) => {
            if (index < keys.length - 1 && tempSchema[key].type === 'object') {
                parentErrors[key] = parentErrors[key] || {};
                tempSchema = tempSchema[key].properties;
                tempData = tempData[key];
                return parentErrors[key];
            } else {
                const fieldSchema = tempSchema[key];
                const fieldValue = tempData[key];
                if (fieldSchema.validation) {
                    parentErrors[key] = fieldSchema.validation(fieldValue, fieldsData);
                }
            }
            return parentErrors;
        }, newErrors);

        setErrors(newErrors);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const newErrors = {};
        let fieldsData = data;
        const validateField = (schema, data, errors, prefix = '') => {
            Object.keys(schema).forEach((key) => {
                const field = schema[key];
                const value = data[key];
                const fieldName = prefix ? `${prefix}.${key}` : key;
                if (field.validation) {
                    errors[key] = field.validation(value, fieldsData);
                }

                if (field.type === 'object') {
                    errors[key] = {};
                    validateField(field.properties, value, errors[key], fieldName);
                }
            });
        };

        validateField(schema, data, newErrors);
        // Check if there are any validation errors

        const hasValidationErrors = (errors) => {
            for (let key in errors) {
                if (typeof errors[key] === 'object' && errors[key] !== null) {
                    // If the current property is an object, recurse into it
                    if (hasValidationErrors(errors[key])) {
                        return true;
                    }
                } else if (errors[key]) {
                    // If there's a non-null and non-object property with a truthy value, we found an error
                    return true;
                }
            }
            // If none of the above conditions are met, there are no validation errors
            return false;
        };

        if (hasValidationErrors(newErrors)) {
            setErrors(newErrors);
            return;
        }

        // If the form is valid, handle submission
        onFormSubmit(data);
    };

    return (
        <>
            {status === 'failed' && <Error>{error}</Error>}
            <form className={`form${inline ? ' form-inline' : ''}`} noValidate onSubmit={handleSubmit} autoComplete="off">
                {renderFields(schema, data, inline, errors, handleChange, handleBlur)}
                <Stack className={`gap-3 mb-3 mt-5${inline ? ' offset-md-4' : ''}`}>
                    <Button
                        className="bg-primary bg-opacity-50 bg-opacity-100-hover"
                        type="submit"
                        disabled={status === 'loading'}>
                        {status === 'loading' || <>Submit</>}
                        {status === 'loading' && <>Submitting...</>}
                    </Button>

                    {onFormCancel &&
                        <Button
                            className="bg-secondary bg-opacity-25 bg-opacity-50-hover"
                            type="reset"
                            onClick={onFormCancel}>
                            Cancel
                        </Button>
                    }
                </Stack>
            </form>
        </>
    );
};

export default Form;
